import { useState, useEffect, useRef } from 'react'
import { fetchWithRetries } from '@/utils/http'

interface ClientLocationData {
  country_code: string
  country_name: string
  calling_code: string
  is_eu: boolean
  timezone: {
    name: string
    offset: string
    abbr: string
  } | null
  loaded: boolean
}

// Global state to ensure API is called only once across the entire app
let globalClientLocation: ClientLocationData | null = null
let globalPromise: Promise<ClientLocationData> | null = null

export function useClientLocation() {
  const [clientLocation, setClientLocation] = useState<ClientLocationData>({
    country_code: '',
    country_name: '',
    calling_code: '',
    is_eu: false,
    timezone: null,
    loaded: false
  })
  const isMountedRef = useRef(true)

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const fetchClientLocation = async () => {
      // If we already have the data, use it
      if (globalClientLocation) {
        if (isMountedRef.current) {
          setClientLocation(globalClientLocation)
        }
        return
      }

      // If there's already a request in progress, wait for it
      if (globalPromise) {
        try {
          const result = await globalPromise
          if (isMountedRef.current) {
            setClientLocation(result)
          }
        } catch (error) {
          console.error('Error fetching client location:', error)
          if (isMountedRef.current) {
            setClientLocation({
              country_code: 'US',
              country_name: 'United States',
              calling_code: '1',
              is_eu: false,
              timezone: null,
              loaded: true
            })
          }
        }
        return
      }

      // Create new request
      globalPromise = (async () => {
        try {
          const result = await fetchWithRetries(`${process.env.NEXT_PUBLIC_BACKEND_URL}/ipdata`, {
            timeout: 4000
          })

          if (!result || result.error || result.statusCode >= 400) {
            console.error('No response from ipdata API', result)
            const fallbackData: ClientLocationData = {
              country_code: 'US',
              country_name: 'United States',
              calling_code: '1',
              is_eu: false,
              timezone: null,
              loaded: true
            }
            globalClientLocation = fallbackData
            return fallbackData
          }

          const locationData: ClientLocationData = {
            country_code: result.country_code || 'US',
            country_name: result.country_name || 'United States',
            calling_code: result.calling_code || '1',
            is_eu: result.is_eu || false,
            timezone: result.time_zone || null,
            loaded: true
          }

          globalClientLocation = locationData
          return locationData
        } catch (error) {
          console.error('Error fetching client location:', error)
          const fallbackData: ClientLocationData = {
            country_code: 'US',
            country_name: 'United States',
            calling_code: '1',
            is_eu: false,
            timezone: null,
            loaded: true
          }
          globalClientLocation = fallbackData
          return fallbackData
        } finally {
          globalPromise = null
        }
      })()

      try {
        const result = await globalPromise
        if (isMountedRef.current) {
          setClientLocation(result)
        }
      } catch (error) {
        console.error('Error in client location fetch:', error)
      }
    }

    fetchClientLocation()
  }, [])

  return clientLocation
}
